import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { axiosInstance } from "../../../utils/axiosInstance";
import LocationFormFields, {
  EMPTY_LOCATION_FORM,
  LocationFormValues,
} from "../locations/LocationFormFields";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (message: string) => void;
};

export default function AddLocation({ open, onClose, onCreated }: Props) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [values, setValues] = useState<LocationFormValues>(EMPTY_LOCATION_FORM);

  useEffect(() => {
    if (!open) return;
    setValues(EMPTY_LOCATION_FORM);
    setErrors({});
  }, [open]);

  const patch = (next: Partial<LocationFormValues>) =>
    setValues((prev) => ({ ...prev, ...next }));

  const save = async () => {
    setLoading(true);
    setErrors({});

    // The API takes form-style strings: checkboxes are only present when ticked.
    const payload: Record<string, string> = {
      name: values.name,
      address: values.address,
      postcode: values.postcode,
      latitude: values.latitude,
      longitude: values.longitude,
      email: values.email,
      phone: values.phone,
      layoutLink: values.layoutLink,
      layoutId: values.layoutId,
      apiId: values.apiId,
      reviewlink: values.reviewlink,
      locationPin: values.locationPin,
      open: values.open,
      close: values.close,
      dry: values.dry,
      pet8kg: values.pet8kg,
      pet14kg: values.pet14kg,
      petdry: values.petdry,
      wash8kg: values.wash8kg,
      wash14kg: values.wash14kg,
      wash18kg: values.wash18kg,
      alerts_enabled: values.alerts.toString(),
      siteStatus: values.siteStatus,
    };
    if (values.hasPawwash) payload.hasPawwash = "true";
    if (values.is24) payload.is24 = "true";
    if (values.alerts) payload.alerts = "true";

    const nitro = Number(values.nitroCmmsLocationId.trim());
    if (values.nitroCmmsLocationId.trim() && Number.isFinite(nitro) && nitro > 0) {
      payload.nitro_cmms_location_id = String(nitro);
    }

    try {
      const res = await axiosInstance.post("/location", payload);
      if (res.data.success === false && res?.data?.validation === false) {
        const validationErrors: Record<string, string> = {};
        res?.data.message.forEach((element: any) => {
          validationErrors[element.context.key] = element.message;
        });
        setErrors(validationErrors);
      } else if (res.data.success === true) {
        onCreated("Location added");
      } else {
        setErrors({ general: "Failed to add the location." });
      }
    } catch (error: any) {
      console.log(error);
      setErrors({
        general: error?.response?.data?.message || "Failed to add the location.",
      });
    }
    setLoading(false);
  };

  const hasFieldErrors = Object.keys(errors).some((k) => k !== "general");

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Add location
        <IconButton aria-label="Close" onClick={onClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {errors.general ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        ) : null}
        {hasFieldErrors ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Check the highlighted fields.
          </Alert>
        ) : null}
        <LocationFormFields
          mode="add"
          values={values}
          onChange={patch}
          errors={errors}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={loading}>
          {loading ? "Saving…" : "Add location"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
